import React, { useState, useRef } from "react";
import { Form, Button, Container, Alert } from "react-bootstrap";
import AdminSidebar from "../../../components/admin/sidebar/adminSidebar";
import ReactQuill from "react-quill";
import DOMPurify from "dompurify"; // HTML 정화를 위해 DOMPurify 사용
import axiosInstance from "../../../api/axios";
import "react-quill/dist/quill.snow.css";
import "./adminProjectWrite.css";

export default function AdminProjectsWrite() {
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    attachment: null,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();

  const handleTitleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleContentChange = (value) => {
    setFormData({ ...formData, content: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0]; // 선택된 파일
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (file) {
      if (file.size > maxSize) {
        alert("파일 크기가 10MB를 초과할 수 없습니다."); // 경고 메시지 표시
        e.target.value = ""; // 선택 초기화
        setFormData({
          ...formData,
          attachment: null, // 파일 초기화
        });
      } else {
        setFormData({
          ...formData,
          attachment: file, // 유효한 파일 저장
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { title, content, attachment } = formData;

    if (!title || !content) {
      setError("모든 필드를 입력해주세요.");
      return;
    }

    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      let attachmentPath = null;

      // 첨부파일이 있을 경우 Presigned URL 요청 및 S3 업로드
      if (attachment) {
        const base64FileName = btoa(encodeURIComponent(attachment.name));

        const presignedUrlResponse = await axiosInstance.get(
          "/api/project/presigned-url",
          {
            params: {
              fileName: base64FileName,
              fileSize: attachment.size, // 파일 크기 전달
            },
          }
        );

        const presignedUrl = presignedUrlResponse.data.data;

        // S3에 파일 업로드
        await axiosInstance.put(presignedUrl, attachment, {
          headers: {
            "Content-Type": attachment.type || "application/octet-stream",
          },
          withCredentials: false,
        });

        // 파일 경로 저장
        attachmentPath = presignedUrl.split("?")[0];
      }

      // 게시물 저장
      const sanitizedContent = DOMPurify.sanitize(content);
      const requestPayload = {
        title,
        content: sanitizedContent,
        attachmentPath, // 첨부파일이 없으면 null로 전달
      };

      const response = await axiosInstance.post(
        "/api/project/write",
        requestPayload
      );

      if (response.data.resultCode === "200") {
        setSuccess(true);
        setFormData({
          title: "",
          content: "",
          attachment: null,
        });

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        setError("게시물 등록에 실패했습니다.");
      }
    } catch (err) {
      console.error("Error submitting project:", err);
      setError("서버와의 통신 중 문제가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminSidebar>
      <Container className="my-5">
        <h2 className="text-center mb-4">연구 프로젝트 등록</h2>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && (
          <Alert
            variant="success"
            onClose={() => setSuccess(false)}
            dismissible
          >
            게시물이 성공적으로 등록되었습니다.
          </Alert>
        )}

        <Form onSubmit={handleSubmit} className="project-write-form">
          <Form.Group className="mb-3" controlId="formTitle">
            <Form.Label>제목</Form.Label>
            <Form.Control
              type="text"
              placeholder="게시물 제목을 입력하세요"
              name="title"
              value={formData.title}
              onChange={handleTitleChange}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formContent">
            <Form.Label>내용</Form.Label>
            <ReactQuill
              theme="snow"
              value={formData.content}
              onChange={handleContentChange}
              modules={{
                toolbar: [
                  [{ header: "1" }, { header: "2" }, { font: [] }],
                  [{ size: [] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ color: [] }, { background: [] }],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link"],
                  ["clean"],
                ],
              }}
              placeholder="게시물 내용을 입력하세요"
            />
          </Form.Group>

          <Form.Group controlId="attachment" className="mb-3">
            <Form.Label>첨부파일</Form.Label>
            <Form.Control
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </Form.Group>

          <div className="text-center">
            <Button
              variant="primary"
              type="submit"
              className="w-100"
              disabled={loading}
            >
              {loading ? "등록 중..." : "등록"}
            </Button>
          </div>
        </Form>
      </Container>
    </AdminSidebar>
  );
}
